import { ShowBase, useShowContext } from "ra-core";
import { Link } from "react-router";

import { Badge } from "@/components/ui/badge";

import { usePermission } from "../providers/usePermission";
import { AddMembershipForm, EndMembershipButton } from "./CompanyEditor";
import {
  AddIdentifierForm,
  ContactEditForm,
  RemoveIdentifierButton,
} from "./ContactEditor";
import { DealCreateDialog } from "./DealCreateDialog";
import { type CustomField, customDetailFields } from "./details";
import { GraphDetails } from "./GraphDetails";
import { EndLinkButton, GraphLinkEditor } from "./GraphLinkEditor";
import { GraphMergeButton } from "./GraphMergeButton";
import { GraphError, GraphLoading } from "./GraphStatus";
import { GraphTimeline } from "./GraphTimeline";
import { FactDot, RecordHeader } from "./record/RecordHeader";
import { RecordCard, RecordRow } from "./record/RecordCard";
import { type ExternalId, ExternalIdsCard } from "./record/ExternalIdsCard";
import { type Assignment, TeamCard } from "./record/TeamCard";
import { linkTypeLabel } from "./linkLabels";
import { MUTED, RECORD_LINK, roleText } from "./record/styles";
import { SignalBadge } from "./SignalBadge";
import { type Signal, signalDetail } from "./signals";
import { useSaveDetails } from "./useSaveDetails";

const ID_LABEL: Record<string, string> = {
  email: "Email",
  phone: "Phone",
  nmls: "NMLS",
  dre: "DRE",
  mls: "MLS",
};

interface GraphContact {
  id: string;
  signals?: Signal[];
  first_name: string;
  last_name: string;
  types: { type_id: string; is_primary: boolean }[];
  identifiers: {
    id: string;
    id_type: string;
    value: string;
    is_primary?: boolean;
    label?: string | null;
  }[];
  external_ids?: ExternalId[];
  assignments?: Assignment[];
  /** State licenses (0020, W15 M4). */
  licenses?: {
    id: string;
    license_type: string;
    license_label: string;
    state: string;
    license_number: string | null;
    status: string;
    valid_to: string | null;
  }[];
  affiliations: {
    id: string;
    company_id: string;
    company_name: string;
    parent_company_id: string | null;
    parent_company_name: string | null;
    role: string | null;
  }[];
  links: {
    id: string;
    link_type_id: string;
    from_object_type: string;
    from_id: string;
    to_object_type: string;
    to_id: string;
    from_name: string | null;
    to_name: string | null;
  }[];
  deals: { deal_id: string; deal_name: string; role: string }[];
  deal_graph?: {
    deal_id: string;
    deal_name: string;
    contact_id: string;
    first_name: string;
    last_name: string;
    role: string;
    company_id?: string | null;
    company_name?: string | null;
  }[];
  merged_into_id?: string | null;
  custom_fields?: CustomField[];
}

/** The tenant's custom contact fields (W8 D5); nothing when none are defined. */
function ContactDetails({ contact }: { contact: GraphContact }) {
  const fields = customDetailFields(contact.custom_fields ?? []);
  const { save, isSaving } = useSaveDetails("contacts", contact.id, fields);
  const canEdit = usePermission("edit_details");
  if (!fields.length) return null;
  return (
    <div className="rounded-xl border bg-card px-5 py-4 shadow-xs">
      <GraphDetails
        title="Details"
        fields={fields}
        onSave={save}
        isSaving={isSaving}
        canEdit={canEdit}
      />
    </div>
  );
}

export function GraphContactShow() {
  return (
    <ShowBase queryOptions={{ onError: () => undefined }}>
      <ContactGraph />
    </ShowBase>
  );
}

/**
 * A contact (W12 U2): header, then three columns: About on the left, the
 * activity (tasks and timeline) in the middle, and the records this person is
 * tied to on the right. One column on narrow screens.
 */
function ContactGraph() {
  const { record, isPending, error } = useShowContext<GraphContact>();
  const canMerge = usePermission("merge_contacts");
  const canLink = usePermission("links");
  const canEdit = usePermission("edit_details");
  const canCreateDeal = usePermission("create_records");
  if (isPending) return <GraphLoading shape="record" />;
  if (error || !record) return <GraphError error={error} noun="contact" />;

  const name = `${record.first_name ?? ""} ${record.last_name ?? ""}`.trim();
  const live = !record.merged_into_id;
  const editable = canEdit && live;
  const types = [...(record.types ?? [])].sort(
    (a, b) => Number(b.is_primary) - Number(a.is_primary),
  );
  const primaryCompany = record.affiliations?.[0];
  const email = record.identifiers?.find((i) => i.id_type === "email");
  const phone = record.identifiers?.find((i) => i.id_type === "phone");
  const isStaff = types.some(
    (t) => t.type_id === "employee" || t.type_id === "loan_officer",
  );

  return (
    <div className="space-y-6">
      <RecordHeader
        name={name}
        chips={
          <>
            {types.map((type) => (
              <Badge
                key={type.type_id}
                variant={type.is_primary ? "default" : "secondary"}
                className="font-normal"
              >
                {type.type_id}
              </Badge>
            ))}
            {(record.signals ?? []).map((signal) => (
              <SignalBadge
                key={signal.id}
                typeId={signal.type_id}
                title={signalDetail(signal)}
              />
            ))}
          </>
        }
        facts={
          <>
            {primaryCompany ? (
              <Link
                className={RECORD_LINK + " font-normal text-muted-foreground"}
                to={`/companies/${primaryCompany.company_id}/show`}
              >
                {primaryCompany.company_name}
              </Link>
            ) : null}
            {primaryCompany && (email || phone) ? <FactDot /> : null}
            {email ? <span>{email.value}</span> : null}
            {email && phone ? <FactDot /> : null}
            {phone ? <span>{phone.value}</span> : null}
          </>
        }
        actions={
          editable ? (
            <>
              <ContactEditForm contact={record} />
              {canCreateDeal ? (
                <DealCreateDialog
                  borrower={{ id: record.id, label: name }}
                  suggestedName={`${record.last_name || record.first_name || ""} purchase`.trim()}
                />
              ) : null}
            </>
          ) : null
        }
      >
        {live ? null : (
          <p className="rounded-md bg-muted px-3 py-2 text-sm">
            Merged into{" "}
            <Link
              className={RECORD_LINK}
              to={`/contacts/${record.merged_into_id}/show`}
            >
              surviving contact
            </Link>
          </p>
        )}
      </RecordHeader>

      <div className="grid grid-cols-[minmax(0,1fr)] items-start gap-6 lg:grid-cols-[minmax(0,20rem)_minmax(0,1fr)] xl:grid-cols-[minmax(0,20rem)_minmax(0,1fr)_minmax(0,22rem)] 2xl:grid-cols-[minmax(0,22rem)_minmax(0,1fr)_minmax(0,24rem)]">
        <div className="space-y-6">
          <RecordCard
            title="Identifiers"
            isEmpty={(record.identifiers ?? []).length === 0 && !editable}
          >
            {(record.identifiers ?? []).length ? (
              <dl className="grid grid-cols-[4rem_1fr_auto] items-center gap-x-3 gap-y-1.5 text-sm">
                {record.identifiers.map((id) => (
                  <div key={id.id} className="group contents">
                    <dt className="text-muted-foreground">
                      {ID_LABEL[id.id_type] ?? id.id_type}
                    </dt>
                    <dd className="min-w-0 break-all">
                      {id.value}
                      {/* Only worth saying when there is more than one. */}
                      {id.is_primary &&
                      record.identifiers.filter((o) => o.id_type === id.id_type)
                        .length > 1 ? (
                        <span className={MUTED}> · primary</span>
                      ) : null}
                      {id.label ? (
                        <span className={MUTED}> · {id.label}</span>
                      ) : null}
                    </dd>
                    <dd className="opacity-60 group-hover:opacity-100">
                      {editable ? (
                        <RemoveIdentifierButton
                          identifierId={id.id}
                          label={`${id.id_type} ${id.value}`}
                        />
                      ) : null}
                    </dd>
                  </div>
                ))}
              </dl>
            ) : (
              <p className="text-sm text-muted-foreground">None</p>
            )}
            {editable ? <AddIdentifierForm contactId={record.id} /> : null}
          </RecordCard>

          {(record.licenses ?? []).length ? (
            <RecordCard title="Licenses" count={record.licenses?.length}>
              <ul>
                {(record.licenses ?? []).map((license) => (
                  <RecordRow
                    key={license.id}
                    primary={
                      <>
                        <span className="font-medium">{license.state}</span>{" "}
                        <span className={MUTED}>{license.license_label}</span>
                      </>
                    }
                    secondary={[
                      license.license_number
                        ? `#${license.license_number}`
                        : null,
                      license.status === "active" ? null : license.status,
                      license.valid_to ? `until ${license.valid_to}` : null,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  />
                ))}
              </ul>
            </RecordCard>
          ) : null}

          <ExternalIdsCard ids={record.external_ids} />

          {/* A merged tombstone's values are history; edit the survivor's. */}
          {live ? <ContactDetails contact={record} /> : null}

          {isStaff ? (
            <RecordCard title="OPTAH / Coaching">
              <p className="text-sm text-muted-foreground">Coming.</p>
            </RecordCard>
          ) : null}

          {canMerge && live ? (
            <RecordCard title="Duplicates">
              <GraphMergeButton
                loserId={record.id}
                firstName={record.first_name}
                lastName={record.last_name}
                summary={{
                  identifiers: record.identifiers?.length ?? 0,
                  types: record.types?.length ?? 0,
                  affiliations: record.affiliations?.length ?? 0,
                  links: record.links?.length ?? 0,
                  deals: record.deals?.length ?? 0,
                }}
              />
            </RecordCard>
          ) : null}
        </div>

        <div className="min-w-0 space-y-6 xl:order-none">
          <RecordCard title="Activity" className="pb-5">
            <GraphTimeline resource="contacts" id={record.id} />
          </RecordCard>
        </div>

        {/* Two across under the others when there is no room for a third column. */}
        <div className="grid grid-cols-[minmax(0,1fr)] gap-6 lg:col-span-2 lg:grid-cols-2 xl:col-span-1 xl:grid-cols-1">
          <RecordCard
            title="Team / affiliations"
            count={record.affiliations?.length ?? 0}
          >
            {(record.affiliations ?? []).length ? (
              <ul>
                {record.affiliations.map((aff) => (
                  <RecordRow
                    key={aff.id}
                    primary={
                      <Link
                        className={RECORD_LINK}
                        to={`/companies/${aff.company_id}/show`}
                      >
                        {aff.company_name}
                      </Link>
                    }
                    secondary={[
                      aff.role ? roleText(aff.role) : null,
                      aff.parent_company_name
                        ? `under ${aff.parent_company_name}`
                        : null,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                    trailing={
                      editable ? (
                        <EndMembershipButton
                          affiliationId={aff.id}
                          label={`membership of ${aff.company_name}`}
                        />
                      ) : null
                    }
                  />
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">None</p>
            )}
            {editable ? <AddMembershipForm contactId={record.id} /> : null}
          </RecordCard>

          <RecordCard
            title="Deals"
            count={record.deals?.length ?? 0}
            isEmpty={(record.deals ?? []).length === 0}
          >
            <ul>
              {(record.deals ?? []).map((deal) => (
                <RecordRow
                  key={`${deal.deal_id}-${deal.role}`}
                  primary={
                    <>
                      <Link
                        className={RECORD_LINK}
                        to={`/deals/${deal.deal_id}/show`}
                      >
                        {deal.deal_name}
                      </Link>{" "}
                      <span className={MUTED}>({roleText(deal.role)})</span>
                    </>
                  }
                />
              ))}
            </ul>
          </RecordCard>

          <RecordCard
            title="Loan triangle"
            count={record.deal_graph?.length ?? 0}
            isEmpty={(record.deal_graph ?? []).length === 0}
          >
            <ul>
              {(record.deal_graph ?? []).map((party) => (
                <RecordRow
                  key={`${party.deal_id}-${party.contact_id}-${party.role}`}
                  primary={
                    <>
                      <Link
                        className={RECORD_LINK}
                        to={`/contacts/${party.contact_id}/show`}
                      >
                        {party.first_name} {party.last_name}
                      </Link>{" "}
                      <span className={MUTED}>— {roleText(party.role)}</span>
                      {party.company_id && party.company_name ? (
                        <>
                          {" "}
                          <span className={MUTED}>at</span>{" "}
                          <Link
                            className={RECORD_LINK + " font-normal"}
                            to={`/companies/${party.company_id}/show`}
                          >
                            {party.company_name}
                          </Link>
                        </>
                      ) : null}{" "}
                      <span className={MUTED}>on</span>{" "}
                      <Link
                        className={RECORD_LINK + " font-normal"}
                        to={`/deals/${party.deal_id}/show`}
                      >
                        {party.deal_name}
                      </Link>
                    </>
                  }
                />
              ))}
            </ul>
          </RecordCard>

          <TeamCard assignments={record.assignments} />

          <RecordCard title="Related" count={record.links?.length ?? 0}>
            {(record.links ?? []).length ? (
              <ul>
                {record.links.map((link) => {
                  const otherId =
                    link.from_id === record.id ? link.to_id : link.from_id;
                  const isFrom = link.from_id === record.id;
                  const otherName = isFrom ? link.to_name : link.from_name;
                  const otherType = isFrom
                    ? link.to_object_type
                    : link.from_object_type;
                  return (
                    <RecordRow
                      key={link.id}
                      primary={
                        <>
                          <span className={MUTED}>
                            {linkTypeLabel({
                              link_type_id: link.link_type_id,
                              direction: isFrom ? "outgoing" : "incoming",
                            })}
                            :
                          </span>{" "}
                          {otherType === "contact" ? (
                            <Link
                              className={RECORD_LINK}
                              to={`/contacts/${otherId}/show`}
                            >
                              {otherName || "related contact"}
                            </Link>
                          ) : (
                            // A property (0016): no page yet (W14 P3).
                            <span>{otherName}</span>
                          )}
                        </>
                      }
                      trailing={
                        canLink ? (
                          <EndLinkButton
                            linkId={link.id}
                            label={otherName ?? ""}
                          />
                        ) : null
                      }
                    />
                  );
                })}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">None</p>
            )}
            {canLink && live ? (
              <GraphLinkEditor objectType="contact" objectId={record.id} />
            ) : null}
          </RecordCard>
        </div>
      </div>
    </div>
  );
}
